import TeamsSpinner from "@edi/fe-common/dist/components/teamsSpinner/teamsSpinner";
import { themeDark, themeHighContrast, themeLight } from "@edi/fe-common/dist/themes";
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { loadTheme, mergeStyleSets, mergeThemes, PrimaryButton, ThemeProvider, useTheme } from '@fluentui/react';
import { initializeFileTypeIcons } from '@fluentui/react-file-type-icons';
import * as microsoftTeams from "@microsoft/teams-js";
import { Settings } from "luxon";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, Router, useHistory } from "react-router-dom";
import TeamsImage from "../../common/components/teamsImage/teamsImage";
import { ImageName } from "../../common/components/teamsImage/teamsImage.types";
import { PreviewWrapper } from "../../docLab/components/files/filePreview/filePreview.base";
import { appInsights, browserHistory } from "../../modules/appInsights/appInsights";
import teamsAuthService from "../../modules/authentication/teamsAuthService";
import currentUser from "../../modules/authentication/currentUser";
import webAuthService from "../../modules/authentication/webAuthService";
import { initI18Next } from "../../modules/internationalization/config";
import { themeDark as appThemeDark } from "../../themes/dark.base";
import { themeHighContrast as appThemeHightContrast } from "../../themes/hc.base";
import { themeLight as appThemeLight } from "../../themes/light.base";
import { useOnMount } from "../../utilities/hooks";
import LoginPopup from "./loginPopup/loginPopup";
import Teams from "./teams/teams";
import { parseContextQueryParam } from '../utilities/queryStringUtils';
import styles from './app.module.css';
import logger from '../../modules/logging/logger';
//import { redirectUriEmpty } from "@azure/msal-common/dist/error/ClientConfigurationErrorCodes";

const classNames = mergeStyleSets({
  root: {
    height: "100%"
  }
});

initI18Next();

const App = () => {
  const [theme, setTheme] = useState<string | undefined>("Light");
  const [authInitialized, setAuthInitialized] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [context, setContext] = useState<microsoftTeams.app.Context | undefined>(undefined);
  const startTheme = useTheme();
  const { t } = useTranslation(['authentication', 'common']);
  const [base, app] = useMemo(() => getPartialThemes(theme), [theme]);
  const merged = useMemo(() => mergeThemes(mergeThemes(startTheme, base), app), [base, app, startTheme]);

  // FIX FluentUI bug for custom components imported by fe-common
  useEffect(() => void loadTheme(merged), [theme]); //eslint-disable-line react-hooks/exhaustive-deps

  useOnMount(() => {
    initializeFileTypeIcons();
    initializeIcons();

    if (window.self !== window.top || window.location.pathname.startsWith('/login'))
      teamsInit();
    else
      webInit();

    
    window.onerror = function(message, source, lineno, colno, error) {
      console.error('Uncaught error: ', message);
      console.error('Source: ', source);
      console.error('Line: ', lineno, ' Column: ', colno);
      console.error('Error object: ', error);
    };

  });

  const webInit = useCallback(async () => {
    try {
      await initializeAuth();
      const success = await attemptTokenRetrieval();

      if (success) {
        appInsights.setAuthenticatedUserContext(webAuthService.currentUserPrincipalName ?? '', webAuthService.currentUserId);

        try {
          await fetchUserProfile();
          setTimeout(() => {
                      
            logger.info("fetchUserProfile", {
              userId: webAuthService.currentUserId,
              timestamp: new Date().toISOString(),
            });
          
            setAuthSuccess(true);
            setAccessDenied(false);
          }, 0);

          await setupTeamsContext();
        } 
        catch (error) {
          logger.error(error as Error, {
            userId: webAuthService.currentUserId,
            step: "fetchUserProfile"
          });

          setAuthError(true);
          setAccessDenied(true);
        }
      } else {
        setAuthError(true);
      }
    } 
    catch (error) {
      logger.error(error as Error, {
        userId: webAuthService.currentUserId,
        step: "Error during web initialization"
      });

      setAuthError(true);
      setAccessDenied(true);
    }
  }, []);

  const initializeAuth = async () => {
    try {
      await webAuthService.init();
      setAuthInitialized(true);
      initI18Next('en');
      Settings.defaultLocale = 'en';
    }
    catch(error)  {
      logger.error(error as Error, {
        userId: webAuthService.currentUserId,
        step: "Error during initializeAuth"
      });
    }
  };

  const attemptTokenRetrieval = async () => { 
    try { 
      const [success] = await webAuthService.tryGetAccessToken();
      return success;
    } 
    catch(error) { 
      logger.error(error as Error,
        { userId: webAuthService.currentUserId,
        step: "Error during attemptTokenRetrieval" }
      );
    } 
  };

  const fetchUserProfile = async () => {
    try {
      await Promise.race([
        currentUser.getUserProfile(webAuthService.currentUserId ?? ''),
        new Promise((_, reject) => setTimeout(() => reject("Timeout"), 30000))
      ]);
    } 
    catch (err) {
      logger.error(err as Error, {
        userId: webAuthService.currentUserId,
        step: "Errore nel recupero del profilo utente"
      });
      throw err;
    }
  };

  const setupTeamsContext = async () => {
    try {
      const { pathname, search } = window.location;
      const customSubEntityId = parseContextQueryParam(search, pathname) ?? '';

      if (customSubEntityId) {
        const context = await microsoftTeams.app.getContext();
        const isNewTeams = context.page?.subPageId !== undefined;
        const pageContext = context.page ?? {};

        const customContext = {
          ...context,
          page: {
            ...pageContext,
            id: pageContext.id ?? "",
            subPageId: isNewTeams ? customSubEntityId : undefined
          },
        };

        setContext(customContext);
      } else {
        console.warn("SubEntityId not found, a fallback may be necessary.");
      }
    }    
    catch(error) { 
      logger.error(error as Error,
        { userId: webAuthService.currentUserId,
        step: "Error during setupTeamsContext" }
      );
    } 
  };

  const teamsInit = useCallback(async () => {
    try {
      await microsoftTeams.app.initialize();

      // Registra handler per il tema
      microsoftTeams.app.registerOnThemeChangeHandler(setTheme);

      // Ottiene il contesto
      const ctx = await microsoftTeams.app.getContext();

      setContext(ctx);
      setTheme(ctx.app.theme);

      // Continua con il resto del codice
      const initSuccess = await initializeTeamsAuthService(ctx);

      if (!initSuccess) {
        console.error("Failed to initialize TeamsAuthService.");
        setAuthError(true);
        return;
      }
      else
        setAuthError(false);

      await handleAuthService(ctx);
    } catch (error) {
      logger.error(error as Error, {
        userId: webAuthService.currentUserId,
        step: "Error initializing Teams context",
      });
    }
  }, []);

  const initializeTeamsAuthService = async (ctx: microsoftTeams.app.Context) => {
    try{
      const initSuccess = await teamsAuthService.init();
      if (initSuccess) {
        initI18Next(ctx.app.locale);
        Settings.defaultLocale = ctx.app.locale;
      }

      return initSuccess;
    }
    catch (error) {
      logger.error(error as Error, {
        userId: webAuthService.currentUserId,
        step: "Error initializing Teams auth service"
      });
    }

    return false;
  };

  const handleAuthService = async (ctx: microsoftTeams.app.Context) => {
    if (window.parent === window.self) return;

    try {
      const [success, error] = await teamsAuthService.tryGetAccessToken();
      if (success) {
        const userPrincipalName = teamsAuthService.currentUserPrincipalName ?? '';
        const userId = teamsAuthService.currentUserId;

        appInsights.setAuthenticatedUserContext(userPrincipalName, userId);
        try {
          await Promise.race([
            currentUser.getUserProfile(userId ?? ''),
            new Promise((_, reject) => setTimeout(() => reject("Timeout"), 5000))
          ]);

          setTimeout(() => {
            setAuthSuccess(true);
            setAuthInitialized(true);
            setAccessDenied(false);
          }, 0);
        } catch (profileError) {
          console.error("Errore durante il recupero del profilo utente:", profileError);
          setAuthError(true);
          setAccessDenied(true);
        }
      } else {
        console.error("Failed to retrieve access token:", error);
        setAuthError(true);
      }
    }
    catch (error) {
      logger.error(error as Error, {
        userId: webAuthService.currentUserId,
        step: "handleAuthService"
      });
    }
  };

  return (
    <ThemeProvider theme={merged} className={classNames.root}>
      {!authSuccess && !authError && (
        <div className={styles.spinnerContainer}>
          <TeamsSpinner />
        </div>
      )}
      {authError && !accessDenied && (
        <div className={styles.spinnerContainer}>
          <TeamsImage imageName={ImageName.Oops2} style={{ marginBottom: 10 }} />
          <span>{t('errorMessage1')}</span>
          <span>{t('errorMessage2')}</span>
          <PrimaryButton text={t('common:retry')} onClick={() => window.location.reload()} className={styles.retryButton} />
        </div>
      )}
      {authError && accessDenied && (
        <div className={styles.accessDenied}>
          <TeamsImage imageName={ImageName.Forbidden} className={styles.forbiddenImage} />
          <div className={styles.accessDeniedContainer}>
            <div className={styles.accessDeniedText}>{t('accessDenied')}</div>
            <PrimaryButton text={t('common:retry')} onClick={() => window.location.reload()} className={styles.retryButton} />
          </div>
        </div>
      )}
      {authInitialized && !authError && (
        <> 
        <Router history={browserHistory}>
          {!authSuccess &&
            <>
            <Route exact path="/login" component={LoginPopup} />
            </>
          }
          {authSuccess &&
            <>
              <Route path="/teams" render={()=><Teams teamsContext={context}></Teams>}/>
              <Route path="/tab" component={Teams} />
              <Route path="/preview" component={PreviewWrapper} />

              <Route exact path="/" render={() => <Home />}/>
              {/* <Route path="/pdf" component={PdfPreview} /> */}
            </>
          }
        </Router>
      </>
    )}
    </ThemeProvider>
  );
};

const getPartialThemes = (theme: string | undefined) => {
  switch (theme) {
    case "dark": return [themeDark, appThemeDark];
    case "contrast": return [themeHighContrast, appThemeHightContrast];
    default: return [themeLight, appThemeLight];
  }
};

window.addEventListener("unhandledrejection", event => {
  console.error("Promise non gestita:", event.reason);
  event.preventDefault();
});

export default App;

const Home = () => {
  const history = useHistory();

  useEffect(() => {
    setTimeout(() => {
      history.push("/teams");
    }, 0);
  }, [history]);

  return null;
};